/* Merme infinite scroll — shared across collection grid, search, blog listing.
 *
 * Usage (rendered by the section ONLY when a next page exists):
 *   <merme-infinite data-next-url="{{ paginate.next.url }}"
 *                   data-section-id="{{ section.id }}"
 *                   data-grid="#product-grid"
 *                   data-auto-pages="3"
 *                   data-label="products"></merme-infinite>
 *
 * Behavior: hybrid — auto-loads `data-auto-pages` pages via IntersectionObserver,
 * then requires a "Load more" click, then auto again (footer stays reachable).
 * Skeleton cards while fetching (site prefers skeletons over spinners).
 *
 * The element must live INSIDE the container that filter/sort AJAX swaps
 * (#ProductGridContainer): each swap destroys and recreates it from the fresh
 * section render, so its state and next-url always match the active filters —
 * no cross-script coordination needed.
 */
(() => {
  if (customElements.get('merme-infinite')) return;

  class MermeInfinite extends HTMLElement {
    connectedCallback() {
      this.nextUrl = this.dataset.nextUrl;
      this.grid = document.querySelector(this.dataset.grid || '#product-grid');
      if (!this.nextUrl || !this.grid) return;
      this.autoPages = parseInt(this.dataset.autoPages || '3', 10);
      this.label = this.dataset.label || 'items';
      this.loading = false;
      this.autoCount = 0;

      this.status = document.createElement('p');
      this.status.className = 'mi-status visually-hidden';
      this.status.setAttribute('aria-live', 'polite');
      this.sentinel = document.createElement('div');
      this.sentinel.className = 'mi-sentinel';
      // Dawn's base.css has div:empty{display:none} — a boxless sentinel never
      // intersects. The zero-width space defeats :empty (CSS backstop too).
      this.sentinel.textContent = '​';
      this.sentinel.setAttribute('aria-hidden', 'true');
      this.append(this.status, this.sentinel);

      this.io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) this.load();
      }, { rootMargin: '600px 0px' });
      this.io.observe(this.sentinel);
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
    }

    skeletons(count) {
      const tag = this.grid.tagName === 'UL' ? 'li' : 'div';
      const made = [];
      for (let i = 0; i < count; i++) {
        const el = document.createElement(tag);
        el.className = 'mi-skel';
        el.setAttribute('aria-hidden', 'true');
        el.innerHTML = '<div class="mi-skel__media"></div><div class="mi-skel__line"></div>';
        this.grid.append(el);
        made.push(el);
      }
      return made;
    }

    showButton(retry) {
      if (!this.btn) {
        this.btn = document.createElement('button');
        this.btn.type = 'button';
        this.btn.className = 'mi-more';
        this.btn.addEventListener('click', () => {
          this.btn.hidden = true;
          this.autoCount = 0;
          this.io.observe(this.sentinel);
          this.load();
        });
        this.append(this.btn);
      }
      this.btn.textContent = retry ? 'Try again' : 'Load more';
      this.btn.hidden = false;
    }

    finish() {
      this.io.disconnect();
      if (this.btn) this.btn.remove();
      this.sentinel.remove();
    }

    load() {
      if (this.loading || !this.nextUrl) return;
      this.loading = true;
      const url = new URL(this.nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const skels = this.skeletons(3);
      fetch(url.pathname + url.search)
        .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const newGrid = doc.querySelector(this.dataset.grid || '#product-grid');
          skels.forEach((s) => s.remove());
          if (!newGrid) { this.finish(); return; }
          const items = Array.from(newGrid.children);
          items.forEach((it) => this.grid.append(it));
          if (window.MermeWishlist && window.MermeWishlist.paint) window.MermeWishlist.paint();
          this.status.textContent = items.length + ' more ' + this.label + ' loaded';

          // reflect the loaded page in the URL (keep filters, drop section_id)
          const shown = new URL(this.nextUrl, window.location.origin);
          shown.searchParams.delete('section_id');
          history.replaceState(null, '', shown.pathname + shown.search);

          const next = doc.querySelector('merme-infinite');
          this.nextUrl = next ? next.dataset.nextUrl : null;
          if (!this.nextUrl) { this.finish(); return; }

          this.autoCount += 1;
          if (this.autoCount >= this.autoPages) {
            this.io.unobserve(this.sentinel);
            this.showButton(false);
          }
        })
        .catch(() => {
          skels.forEach((s) => s.remove());
          this.io.unobserve(this.sentinel);
          this.showButton(true);
        })
        .finally(() => { this.loading = false; });
    }
  }

  customElements.define('merme-infinite', MermeInfinite);
})();
